import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { User, Mail, Phone, Gift, Calendar, Globe, Ticket, ShieldCheck, Trophy, Headphones, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { IconInput, PasswordInput } from "@/pages/Login";

const EMPTY = { full_name: "", username: "", email: "", phone: "", password: "", confirm_password: "", dob: "", country: "India", admin_code: "", accept_terms: false };

function validate(f, minAge) {
  const e = {};
  if (f.full_name.trim().length < 2) e.full_name = "Enter your full name";
  if (!/^[A-Za-z0-9_.]{3,24}$/.test(f.username)) e.username = "3–24 letters, numbers, _ or .";
  if (!/^\S+@\S+\.\S+$/.test(f.email)) e.email = "Enter a valid email";
  if (f.phone && !/^[0-9 ]{7,15}$/.test(f.phone)) e.phone = "Enter a valid mobile number";
  if (!/^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(f.password)) e.password = "8+ characters with a letter and a number";
  if (f.password !== f.confirm_password) e.confirm_password = "Passwords do not match";
  if (!f.dob) e.dob = "Enter your date of birth";
  else {
    const d = new Date(f.dob); const t = new Date();
    const age = t.getFullYear() - d.getFullYear() - (t < new Date(t.getFullYear(), d.getMonth(), d.getDate()) ? 1 : 0);
    if (age < minAge) e.dob = `You must be at least ${minAge} years old`;
  }
  if (!f.admin_code.trim()) e.admin_code = "Admin code is required to register";
  if (!f.accept_terms) e.accept_terms = "You must accept the terms to continue";
  return e;
}

const Field = ({ label, error, children, id }) => (
  <div>
    <label className="label-cr" htmlFor={id}>{label}</label>
    {children}
    {error && <p data-testid={`${id}-error`} className="mt-1 text-xs text-cr-danger">{error}</p>}
  </div>
);

const BonusAside = ({ bonus }) => (
  <aside className="card-gold hidden p-7 text-center lg:block">
    <div className="mx-auto grid h-16 w-16 place-items-center rounded-full border border-cr-gold/60"><Gift className="h-8 w-8 text-cr-gold" /></div>
    <p className="mt-5 font-display text-lg font-semibold uppercase">{bonus?.label}</p>
    <p className="font-display text-3xl font-extrabold"><span className="gold-text">{bonus?.percent}%</span> <span className="text-xl">UP TO</span></p>
    <p className="font-display text-4xl font-black gold-text">{money(bonus?.cap, { whole: true })}</p>
    <p className="mt-1 text-sm font-semibold uppercase">{bonus?.headline}</p>
    <div className="my-5 h-px bg-cr-line" />
    <ul className="space-y-3 text-left text-sm text-cr-ink/85">
      {[[ShieldCheck, "Secure account controls"], [Trophy, "Five sports covered"], [Headphones, "Ticket-based support"], [Lock, "Responsible gaming tools"]].map(([I, t]) => (
        <li key={t} className="flex items-center gap-3"><I className="h-4 w-4 text-cr-gold" />{t}</li>
      ))}
    </ul>
    <p className="mt-5 text-[11px] text-cr-muted">{bonus?.eligibility}</p>
  </aside>
);

export default function Register() {
  const { config, setUser } = useAuth();
  const nav = useNavigate();
  const comp = config?.compliance || {};
  const [sp] = useSearchParams();
  const [f, setF] = useState({ ...EMPTY, admin_code: (sp.get("code") || "").toUpperCase() });
  const [errs, setErrs] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const v = validate(f, comp.min_age || 18);
    setErrs(v);
    if (Object.keys(v).length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { ...f, phone: f.phone ? `+91 ${f.phone}` : "" });
      setUser(data);
      toast.success("Account created. Please verify your email.");
      nav("/account");
    } catch (e2) {
      setErr(apiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="register-page" className="relative overflow-hidden">
      <img src="/brand/ball.jpg" alt="" className="pointer-events-none absolute -right-10 top-24 hidden w-[380px] opacity-80 xl:block" />
      <div className="relative mx-auto grid max-w-7xl gap-8 px-5 py-10 lg:grid-cols-[280px_1fr] xl:grid-cols-[280px_1fr_200px]">
        <BonusAside bonus={config?.landing?.bonus} />
        <form onSubmit={submit} noValidate data-testid="register-form" className="card-gold p-6 sm:p-9">
          <h1 className="text-center text-3xl font-extrabold uppercase">Create <span className="gold-text">Your Account</span></h1>
          <p className="mt-2 text-center text-cr-ink/75">Join CR Bets — 18+ only. Please play responsibly.</p>
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            <Field label="Full name" id="reg-full-name" error={errs.full_name}><IconInput icon={User} id="reg-full-name" data-testid="reg-full-name-input" placeholder="Enter your full name" value={f.full_name} onChange={set("full_name")} /></Field>
            <Field label="Username" id="reg-username" error={errs.username}><IconInput icon={User} id="reg-username" data-testid="reg-username-input" placeholder="Choose a username" value={f.username} onChange={set("username")} /></Field>
            <Field label="Email address" id="reg-email" error={errs.email}><IconInput icon={Mail} id="reg-email" type="email" data-testid="reg-email-input" placeholder="Enter your email address" value={f.email} onChange={set("email")} /></Field>
            <Field label="Mobile number (optional)" id="reg-phone" error={errs.phone}>
              <div className="flex gap-2"><span className="grid place-items-center rounded-md border border-cr-line bg-[#0d0f0f] px-3 text-sm text-cr-gold">+91</span>
                <div className="flex-1"><IconInput icon={Phone} id="reg-phone" data-testid="reg-phone-input" placeholder="Enter your mobile number" value={f.phone} onChange={set("phone")} /></div></div>
            </Field>
            <Field label="Password" id="reg-password" error={errs.password}><PasswordInput id="reg-password" testid="reg-password-input" placeholder="Create a password" value={f.password} onChange={set("password")} /></Field>
            <Field label="Confirm password" id="reg-confirm" error={errs.confirm_password}><PasswordInput id="reg-confirm" testid="reg-confirm-input" placeholder="Confirm your password" value={f.confirm_password} onChange={set("confirm_password")} /></Field>
            <Field label="Date of birth" id="reg-dob" error={errs.dob}><IconInput icon={Calendar} id="reg-dob" type="date" data-testid="reg-dob-input" value={f.dob} onChange={set("dob")} /></Field>
            <Field label="Country / region" id="reg-country">
              <div className="relative"><Globe className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-cr-muted" />
                <select id="reg-country" data-testid="reg-country-select" value={f.country} onChange={set("country")} className="input-cr appearance-none pl-10">
                  {(comp.countries || ["India"]).map((c) => <option key={c} value={c}>{c}</option>)}
                </select></div>
            </Field>
            <div className="sm:col-span-2"><Field label="Admin code (required)" id="reg-admin-code" error={errs.admin_code}><IconInput icon={Ticket} id="reg-admin-code" data-testid="reg-admin-code-input" placeholder="Enter the code given by your admin" value={f.admin_code} onChange={set("admin_code")} /></Field></div>
          </div>
          <label className="mt-6 flex items-start gap-3 text-sm">
            <input type="checkbox" data-testid="reg-terms-checkbox" checked={f.accept_terms} onChange={set("accept_terms")} className="mt-0.5 h-4 w-4 accent-[#FFC928]" />
            <span>I am {comp.min_age || 18}+ years old and I agree to the <Link to="/legal/terms" className="text-cr-gold">Terms & Conditions</Link> and <Link to="/legal/privacy" className="text-cr-gold">Privacy Policy</Link></span>
          </label>
          {errs.accept_terms && <p data-testid="reg-terms-error" className="mt-1 text-xs text-cr-danger">{errs.accept_terms}</p>}
          {err && <p data-testid="register-error" role="alert" className="mt-4 rounded-md border border-cr-danger/40 bg-cr-danger/10 px-3 py-2 text-sm text-cr-danger">{err}</p>}
          <button disabled={busy} data-testid="register-submit-btn" className="btn-gold mt-6 w-full py-3.5 text-base">{busy ? "Creating account…" : "Register Now"}</button>
          <p className="mt-6 text-center">Already have an account? <Link to="/login" data-testid="register-login-link" className="font-bold text-cr-gold">Login</Link></p>
        </form>
      </div>
    </main>
  );
}
